import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FullScreen, useFullScreenHandle } from 'react-full-screen';
import { useParams } from 'react-router';
import { API, graphqlOperation } from 'aws-amplify';
import moment from 'moment';
import Avatar from '@gym-atoms/Avatar/Avatar';
import staffPlaceholder from '@gym-assets/images/staff-image-placeholder.jpg';
import { getClassPortalSnapshot } from '@gym-graphql/queries';
import type { GetClassPortalSnapshotQuery } from '@gym-src/API';
import styles from './ClassPortalPage.module.scss';

const POLLING_INTERVAL_MS = 30_000;
const DISPLAYED_CLASS_LIMIT = 5;
const CAROUSEL_INTERVAL_MS = 8_000;

type Option<T> = { kind: 'some'; value: T } | { kind: 'none' };

type PortalImage = {
  url: string;
  alt: string;
};

type PortalClass = {
  id: string;
  name: string;
  instructorName: string;
  instructorImage: PortalImage;
  classStart: string;
  classEnd: string;
  slots: string;
};

type PortalSnapshot = {
  generatedAt: string;
  location: {
    name: string;
    logo: Option<PortalImage>;
  };
  classes: PortalClass[];
};

type LoadResult = { kind: 'success'; snapshot: PortalSnapshot } | { kind: 'failure' };

type PortalState =
  | { status: 'loading'; siteLocationId: number }
  | { status: 'ready'; siteLocationId: number; snapshot: PortalSnapshot }
  | { status: 'error'; siteLocationId: number };

type SnapshotResponse = {
  data: GetClassPortalSnapshotQuery;
};

type SnapshotData = GetClassPortalSnapshotQuery['getClassPortalSnapshot'];

const toOption = <T,>(value: T | null | undefined): Option<T> =>
  value === null || value === undefined ? { kind: 'none' } : { kind: 'some', value };

const formatTime = (localDateTime: string) => moment.parseZone(localDateTime).format('hh:mm A');
const getTimeString = () => moment().format('dddd D MMMM h:mm a');

const parseInstructorImage = (
  instructor: SnapshotData['classes'][number]['instructor']
): PortalImage =>
  instructor.image
    ? { url: instructor.image.url, alt: instructor.image.alt }
    : { url: staffPlaceholder, alt: `${instructor.name} placeholder` };

const parseSnapshot = (snapshot: SnapshotData): PortalSnapshot => ({
  generatedAt: snapshot.generatedAt,
  location: {
    name: snapshot.location.name,
    logo: toOption(
      snapshot.location.logo
        ? { url: snapshot.location.logo.url, alt: snapshot.location.logo.alt }
        : undefined
    )
  },
  classes: snapshot.classes.slice(0, DISPLAYED_CLASS_LIMIT).map((classItem) => ({
    id: classItem.id,
    name: classItem.name,
    instructorName: classItem.instructor.name,
    instructorImage: parseInstructorImage(classItem.instructor),
    classStart: formatTime(classItem.startsAt),
    classEnd: formatTime(classItem.endsAt),
    slots: `${classItem.availability.available} / ${classItem.availability.capacity}`
  }))
});

const loadSnapshot = async (siteLocationId: number): Promise<LoadResult> => {
  try {
    const response = (await API.graphql(
      graphqlOperation(getClassPortalSnapshot, { siteLocationId })
    )) as SnapshotResponse;

    return { kind: 'success', snapshot: parseSnapshot(response.data.getClassPortalSnapshot) };
  } catch (error) {
    console.error('Failed to load the Virtuagym class portal snapshot', {
      siteLocationId,
      error
    });
    return { kind: 'failure' };
  }
};

const applyLoadResult = (
  siteLocationId: number,
  result: LoadResult,
  currentState: PortalState
): PortalState => {
  if (currentState.siteLocationId !== siteLocationId) return currentState;
  if (result.kind === 'success') {
    if (
      currentState.status === 'ready' &&
      currentState.snapshot.generatedAt > result.snapshot.generatedAt
    ) {
      return currentState;
    }
    return { status: 'ready', siteLocationId, snapshot: result.snapshot };
  }
  return currentState.status === 'ready' ? currentState : { status: 'error', siteLocationId };
};

const LocationLogo = ({ name, logo }: PortalSnapshot['location']) =>
  logo.kind === 'some' ? (
    <img
      src={logo.value.url}
      className="rounded-full w-12 md:w-14 lg:w-16 xl:w-20 2xl:w-24 h-12 md:h-14 lg:h-16 xl:h-20 2xl:h-24 object-cover p-0.5 lg:p-1 xl:p-1.5 2xl:p-2"
      alt={logo.value.alt}
    />
  ) : (
    <Avatar size="xlarge" label={name.charAt(0) || '?'} />
  );

const PortalHeader = ({
  location,
  timeString
}: {
  location: PortalSnapshot['location'];
  timeString: string;
}) => (
  <div className="flex-shrink-0">
    <div className="flex flex-row justify-center items-center bg-[#fbac19]">
      <LocationLogo {...location} />
      <p className="text-lg lg:text-xl xl:text-2xl 2xl:text-3xl 3xl:text-4xl">{location.name}</p>
    </div>
    <div className="flex flex-row gap-4 justify-center items-center bg-[#ffffff]">
      <p className="w-full text-lg lg:text-xl xl:text-2xl 2xl:text-3xl 3xl:text-4xl text-slate-950 text-center">
        {timeString}
      </p>
    </div>
  </div>
);

const CarouselItem = ({
  name,
  instructorName,
  instructorImage,
  classStart,
  classEnd,
  slots
}: PortalClass) => {
  const { t } = useTranslation();

  return (
    <div
      className={`${styles.carouselItem} flex w-full flex-col gap-1.5 lg:gap-2 xl:gap-3.5 2xl:gap-4 3xl:gap-6 text-center items-center justify-center`}>
      <p className="font-bold text-lg lg:text-xl xl:text-2xl 2xl:text-3xl 3xl:text-4xl">
        {t('CLASS_PORTAL_PAGE.UPCOMING_CLASSES')}
      </p>
      <p className="text-lg lg:text-2xl xl:text-3xl 2xl:text-4xl 3xl:text-5xl font-semibold text-center p-0">
        {classStart} - {classEnd}
      </p>
      <p className="text-base lg:text-xl xl:text-2xl 2xl:text-3xl 3xl:text-4xl font-semibold text-center">
        {name}
      </p>
      <p className="text-base lg:text-xl xl:text-2xl 2xl:text-3xl 3xl:text-4xl text-center">
        {t('CLASS_PORTAL_PAGE.SLOTS_AVAILABLE')}
      </p>
      <p className="text-base lg:text-xl xl:text-2xl 2xl:text-3xl 3xl:text-4xl font-semibold text-center">
        {slots}
      </p>
      <p className="text-base lg:text-xl xl:text-2xl 2xl:text-3xl 3xl:text-4xl text-center">
        {t('CLASS_PORTAL_PAGE.INSTRUCTOR')}
      </p>
      <img
        src={instructorImage.url}
        className="rounded-full w-16 lg:w-20 xl:w-24 2xl:w-32 3xl:w-36 h-16 lg:h-20 xl:h-24 2xl:h-32 3xl:h-36 object-cover"
        alt={instructorImage.alt}
      />
      <p className="text-base lg:text-xl xl:text-2xl 2xl:text-3xl 3xl:text-4xl font-semibold text-center">
        {instructorName}
      </p>
    </div>
  );
};

const ClassCarousel = ({ items }: { items: PortalClass[] }) => {
  const [index, setIndex] = useState(0);
  const currentItem = items[index % items.length];

  useEffect(() => {
    const interval = setInterval(() => {
      setIndex((currentIndex) => (currentIndex + 1) % items.length);
    }, CAROUSEL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [items.length]);

  return (
    <div
      className="h-full w-full flex flex-col items-center justify-center overflow-hidden"
      key={currentItem.id}>
      <CarouselItem {...currentItem} />
    </div>
  );
};

const ClassCard = ({
  name,
  instructorName,
  instructorImage,
  classStart,
  classEnd,
  slots
}: PortalClass) => {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-8 border-b-[#fbac19] border-b divide-x p-1 w-full min-h-0 overflow-hidden">
      <div className="col-span-3 flex flex-col items-center justify-center p-1">
        <p className="text-base lg:text-xl xl:text-2xl 2xl:text-3xl 3xl:text-4xl font-semibold text-center leading-tight break-words line-clamp-2">
          {name}
        </p>
      </div>
      <div className="col-span-3 border-[#fbac19] flex flex-row items-center gap-2 p-2 min-w-0 overflow-hidden">
        <img
          src={instructorImage.url}
          className="flex-shrink-0 rounded-full w-12 lg:w-14 xl:w-16 2xl:w-20 3xl:w-24 h-12 lg:h-14 xl:h-16 2xl:h-20 3xl:h-24 object-cover ring-2 ring-[#fbac19]"
          alt={instructorImage.alt}
        />
        <div className="flex flex-col items-start justify-center min-w-0 overflow-hidden">
          <p className="mt-0 text-base lg:text-lg xl:text-xl 2xl:text-2xl 3xl:text-3xl font-semibold whitespace-nowrap">
            {classStart} - {classEnd}
          </p>
          <p className="mt-0 w-full text-base lg:text-lg xl:text-xl 2xl:text-2xl 3xl:text-3xl font-semibold leading-tight break-words line-clamp-2">
            {instructorName}
          </p>
        </div>
      </div>
      <div className="col-span-2 flex flex-col justify-center items-center">
        <p className="mt-0 text-base lg:text-lg xl:text-xl 2xl:text-2xl 3xl:text-3xl text-center">
          {t('CLASS_PORTAL_PAGE.SLOTS_AVAILABLE')}:
        </p>
        <p className="mt-0 text-xl lg:text-2xl xl:text-3xl 2xl:text-4xl 3xl:text-5xl text-center font-semibold">
          {slots}
        </p>
      </div>
    </div>
  );
};

const ReadyPortal = ({
  snapshot,
  timeString
}: {
  snapshot: PortalSnapshot;
  timeString: string;
}) => {
  const { t } = useTranslation();

  return (
    <div className="h-full w-full flex flex-col">
      <PortalHeader location={snapshot.location} timeString={timeString} />
      {snapshot.classes.length > 0 ? (
        <div className="grid grid-cols-8 divide-x flex-1 min-h-0">
          <div className="col-span-3 flex flex-col items-center min-h-0">
            <ClassCarousel items={snapshot.classes} />
          </div>
          <div
            className="col-span-5 grid min-h-0 border-[#fbac19]"
            style={{
              gridTemplateRows: `repeat(${snapshot.classes.length}, minmax(0, 1fr))`
            }}>
            {snapshot.classes.map((classItem) => (
              <ClassCard {...classItem} key={classItem.id} />
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center flex flex-1 min-h-0 items-center">
          <p className="w-full text-3xl">{t('CLASS_PORTAL_PAGE.NO_CLASSES')}</p>
        </div>
      )}
    </div>
  );
};

const MainColumn = () => {
  const fullScreenHandle = useFullScreenHandle();
  const { siteLocationId: siteLocationIdParam } = useParams<{ siteLocationId: string }>();
  const siteLocationId = Number.parseInt(siteLocationIdParam, 10);
  const { t } = useTranslation();
  const [timeString, setTimeString] = useState(getTimeString());
  const [portalState, setPortalState] = useState<PortalState>({
    status: 'loading',
    siteLocationId
  });

  useEffect(() => {
    setPortalState({ status: 'loading', siteLocationId });

    const refreshSnapshot = async () => {
      const result = await loadSnapshot(siteLocationId);
      setPortalState((currentState) => applyLoadResult(siteLocationId, result, currentState));
    };

    refreshSnapshot();
    const pollingInterval = setInterval(refreshSnapshot, POLLING_INTERVAL_MS);
    return () => clearInterval(pollingInterval);
  }, [siteLocationId]);

  useEffect(() => {
    const clockInterval = setInterval(() => setTimeString(getTimeString()), 1000);
    return () => clearInterval(clockInterval);
  }, []);

  return (
    <FullScreen handle={fullScreenHandle}>
      <div
        className={`bg-[#000000] text-white flex flex-col overflow-hidden ${
          fullScreenHandle.active ? 'w-screen h-screen' : 'w-full h-full'
        }`}>
        {!fullScreenHandle.active && (
          <button
            type="button"
            onClick={fullScreenHandle.enter}
            className="block p-4 text-center bg-[#000000] hover:cursor-pointer w-full">
            {t('CLASS_PORTAL_PAGE.ENTER_FULL_SCREEN')}
          </button>
        )}
        {portalState.status === 'ready' ? (
          <div className="flex-1 min-h-0">
            <ReadyPortal snapshot={portalState.snapshot} timeString={timeString} />
          </div>
        ) : (
          <div className="flex flex-1 min-h-0 items-center text-center">
            <p className="w-full text-3xl">
              {t(
                portalState.status === 'loading'
                  ? 'CLASS_PORTAL_PAGE.LOADING'
                  : 'CLASS_PORTAL_PAGE.LOAD_ERROR'
              )}
            </p>
          </div>
        )}
      </div>
    </FullScreen>
  );
};

const ClassPortalPage = () => <MainColumn />;

export default ClassPortalPage;
